import { CloudDownload } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { Field, Formik, FormikProps, useFormikContext } from "formik";
import { TextField } from "formik-mui";
import { useRouter } from "next/router";
import * as React from "react";
import * as yup from "yup";
import AppContext from "../../../../AppContext";
import Maybe from "../../../../components/common/Maybe";
import {
  submitQuoteForm,
  updateCoverageAreaText,
  updateDateText,
  updatePeopleTab,
  updateTripProtectionText,
} from "../../../../components/quote/Form/QuoteHelpers";
import { FullQuoteResult } from "../../../../components/quote/QuoteData.interface";
import { QuoteRequest } from "../../../../components/quote/QuoteRequestData";
import { SavedQuotes } from "../../../../utilities/saved-quotes";

interface Props {
  setQuoteData: React.Dispatch<
    React.SetStateAction<Partial<FullQuoteResult> | null>
  >;
  setQuoteBoxDetails: React.Dispatch<
    React.SetStateAction<{
      coverageDuration: string;
      coverageArea: string;
      tripProtection: string;
      numPeople: string;
    }>
  >;
  agentNum: string;
}

const ResumeButton = ({
  setQuoteData,
  setQuoteBoxDetails,
  agentNum,
}: Props): JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);

  const appState: any = React.useContext(AppContext);
  const router = useRouter();

  const { values, setValues } = useFormikContext();

  const [snackbarOptions, setSnackbarOptions] = React.useState<{
    openSuccess: boolean;
    openError: boolean;
  }>({
    openSuccess: false,
    openError: false,
  });

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOpenSnackbar = (success: boolean) => {
    setSnackbarOptions({
      openSuccess: success,
      openError: !success,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOptions({
      openSuccess: false,
      openError: false,
    });
  };

  const loadQuote = async (values: { quoteId: string }) => {
    setNotFound(false);

    const saveQuote = new SavedQuotes();
    let response = null;

    try {
      response = await saveQuote.getQuote(values.quoteId);
    } catch (e: any) {
      console.error(e);
      handleOpenSnackbar(false);
    }

    if (response) {
      if (response.error?.statusCode === 404) {
        setNotFound(true);
      } else if (response.error) {
        handleOpenSnackbar(false);

        appState.setAppState({
          ...appState.state,
          quoteRequestError: true,
        });
      } else {
        const quoteRequestData = response.quoteData as QuoteRequest;
        const agentNum = response.agentNum as string;
        const affiliateCode = response.affiliateCode ?? '';

        router.replace({
          query: {
            ...router.query,
            agent: agentNum,
            ...(affiliateCode ? { affiliate: affiliateCode } : {}),
          },
        });

        const quoteResultData = await submitQuoteForm(
          quoteRequestData,
          agentNum
        );

        setValues(response.quoteData);
        setQuoteData(quoteResultData);

        appState.setAppState({
          ...appState.state,
          quoteRequestData: quoteRequestData,
          quoteRequestError: false,
        });

        setQuoteBoxDetails({
          coverageDuration: `${updateDateText(
            quoteRequestData.startDate,
            undefined,
            "Start"
          )} ➜ ${updateDateText(quoteRequestData.endDate, undefined, "End")}`,
          coverageArea: updateCoverageAreaText(
            quoteRequestData.usaTrip,
            quoteRequestData.countryObject
          ),
          tripProtection: updateTripProtectionText(
            quoteRequestData.tripProtection
          ),
          numPeople: quoteRequestData.primaryInsured
            ? updatePeopleTab(quoteRequestData.primaryInsured, undefined)
            : "Who needs coverage?",
        });

        handleOpenSnackbar(true);
        handleCloseModal();
      }
    }
  };

  const loadQuoteInit = {
    quoteId: "",
  };

  const saveFormValidationSchema = yup.object({
    quoteId: yup.string().required("Quote ID is required."),
  });

  return (
    <>
      <IconButton aria-label="resume" onClick={handleOpenModal}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 14.1667V2.5"
            stroke="#212121"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M5 9.16667L10 14.1667L15 9.16667"
            stroke="#212121"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M15.8333 17.5H4.16666"
            stroke="#212121"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </IconButton>

      <Dialog open={open} onClose={handleCloseModal} scroll={"body"}>
        <DialogTitle>Retrieve a quote or resume your application</DialogTitle>

        <Formik
          initialValues={loadQuoteInit}
          validationSchema={saveFormValidationSchema}
          onSubmit={loadQuote}
        >
          {(formikProps: FormikProps<{ quoteId: string }>) => {
            return (
              <>
                <DialogContent dividers>
                  <Typography variant="body1">
                    Pick up where you left off by entering your quote ID. This
                    ID can be located in the email we sent to you when you
                    initially saved your quote.
                  </Typography>

                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    rowGap={2}
                    pt={4}
                    mb={3}
                  >
                    <Field
                      component={TextField}
                      placeholder={"Your Quote ID"}
                      name="quoteId"
                      type="text"
                      label="Quote ID"
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      disabled={formikProps.isSubmitting}
                    />
                  </Box>

                  <Maybe test={notFound}>
                    <Typography color={"error"}>
                      The provided quote ID was not found in our system. Please
                      double check your ID and try again.
                    </Typography>
                  </Maybe>
                </DialogContent>

                <DialogActions>
                  <Button
                    onClick={handleCloseModal}
                    disabled={formikProps.isSubmitting}
                  >
                    Close
                  </Button>
                  <LoadingButton
                    onClick={formikProps.submitForm}
                    endIcon={<CloudDownload />}
                    loading={formikProps.isSubmitting}
                    loadingPosition="end"
                    variant="contained"
                  >
                    Load Quote
                  </LoadingButton>
                </DialogActions>
              </>
            );
          }}
        </Formik>

        <Snackbar
          open={snackbarOptions.openError}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert
            severity={"error"}
            onClose={handleCloseSnackbar}
            variant={"filled"}
            sx={{ width: "100%" }}
          >
            Failed to load quote.
          </Alert>
        </Snackbar>
      </Dialog>

      <Snackbar
        open={snackbarOptions.openSuccess}
        onClose={handleCloseSnackbar}
        autoHideDuration={6000}
      >
        <Alert
          severity={"success"}
          onClose={handleCloseSnackbar}
          variant={"filled"}
          sx={{ width: "100%" }}
        >
          Quote loaded.
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResumeButton;
